import { useState, useEffect, useRef } from 'react';
import * as React from 'react';
import {
  Button,
  Input,
  Drawer,
  Avatar,
  Typography,
  Space,
  Alert,
  Tooltip,
  Divider
} from 'antd';
import {
  SendOutlined,
  ClearOutlined,
  UserOutlined,
  StopOutlined,
  ArrowDownOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import useChatBot from '../../hooks/useChatBot';
import { ChatContainer, MessageContainer, MessageBubble, InputContainer, HeaderContainer, LoadingPhrase } from './styles';
import { setChatControlRef } from '../../utils/chatControl';
import flowieIcon from '../../assets/flowie.png';

const { TextArea } = Input;
const { Text, Title } = Typography;

// Componente de ícone da FlowIA usando imagem
const FriendlyAIIcon = ({ size = 24, style = {} }) => {
  return (
    <img
      src={flowieIcon}
      alt="FlowIA"
      width={size}
      height={size}
      style={{
        ...style,
        objectFit: 'contain',
        display: 'block'
      }}
    />
  );
};

// Sanitiza respostas removendo citações/arquivos (ex.: 【…】, [..docx], .pdf/.docx)
const sanitizeResponse = (text = '') => {
  try {
    let t = text;
    t = t.replace(/【[^】]*】/g, ''); // citações estilo 【…】
    t = t.replace(/\[[^\]]*\.(docx|pdf)\]/gi, ''); // referências com arquivo
    t = t.replace(/\b\S+\.(docx|pdf)\b/gi, ''); // nomes de arquivo soltos
    // Colapsa apenas espaços e tabs. \s{2,} incluía \n e apagava
    // toda a separação de parágrafos antes mesmo da formatação rodar.
    t = t.replace(/[ \t]{2,}/g, ' ');
    return t.trim();
  } catch { return text; }
};

// Formata mensagens para melhor legibilidade (listas, parágrafos, código)
const formatMessage = (text = '') => {
  if (!text) return text;
  
  try {
    let formatted = text;
    
    // Normalizar quebras de linha
    formatted = formatted.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    
    // Itens numerados são reconhecidos apenas no início da linha.
    // As regras antigas agiam no meio do texto e destruíam números
    // formatados: "123.456.789-00" virava "123. 456. 789-00".
    formatted = formatted.replace(/^[ \t]*(\d{1,2}[\)\.])[ \t]+/gm, '$1 ');

    // Detectar listas com • ou - no início da linha
    formatted = formatted.replace(/^[ \t]*[•\-][ \t]+/gm, '• ');

    // No máximo uma linha em branco entre blocos
    formatted = formatted.replace(/\n{3,}/g, '\n\n');
    
    // Remover espaços em branco no início/fim de linhas
    formatted = formatted.split('\n').map(l => l.trimRight()).join('\n');
    
    // Remover linha vazia no início
    if (formatted.startsWith('\n')) {
      formatted = formatted.substring(1);
    }
    
    return formatted;
  } catch {
    return text;
  }
};

// Separa blocos de código cercados por ``` do texto comum.
// Uma cerca ainda não fechada (comum durante o streaming) é tratada
// como código até o fim, para não vazar as crases na tela.
const splitCodeSegments = (text = '') => {
  const segments = [];
  const fence = /```([^\n`]*)\n?/g;
  let cursor = 0;
  let match;

  while ((match = fence.exec(text)) !== null) {
    if (match.index > cursor) {
      segments.push({ type: 'text', content: text.slice(cursor, match.index) });
    }

    const language = match[1].trim();
    const codeStart = match.index + match[0].length;
    const closing = text.indexOf('```', codeStart);
    const isOpen = closing === -1;
    const code = text.slice(codeStart, isOpen ? text.length : closing);

    segments.push({ type: 'code', content: code.replace(/\n$/, ''), language });

    cursor = isOpen ? text.length : closing + 3;
    fence.lastIndex = cursor;
  }

  if (cursor < text.length) {
    segments.push({ type: 'text', content: text.slice(cursor) });
  }

  return segments.length > 0 ? segments : [{ type: 'text', content: text }];
};

// Bloco de código: preserva espaços e rola na horizontal em vez de
// estourar a largura da bolha
const CodeBlock = ({ content }) => (
  <pre
    style={{
      background: 'rgba(0, 0, 0, 0.06)',
      border: '1px solid rgba(0, 0, 0, 0.08)',
      borderRadius: '6px',
      padding: '10px 12px',
      margin: '6px 0',
      overflowX: 'auto',
      fontFamily: 'monospace',
      fontSize: '12.5px',
      lineHeight: '1.5',
      whiteSpace: 'pre',
      color: 'inherit'
    }}
  >
    <code>{content}</code>
  </pre>
);

// Componente para renderizar mensagem formatada
const FormattedMessage = ({ content }) => {
  if (!content) return null;

  const segments = splitCodeSegments(sanitizeResponse(content));

  return (
    <div style={{
      lineHeight: '1.4',
      wordBreak: 'break-word'
    }}>
      {segments.map((segment, segmentIndex) => {
        if (segment.type === 'code') {
          return <CodeBlock key={`code-${segmentIndex}`} content={segment.content} />;
        }
        return (
          <div key={`text-${segmentIndex}`}>
            {renderTextLines(formatMessage(segment.content))}
          </div>
        );
      })}
    </div>
  );
};

// Renderiza as linhas de um trecho de texto (listas, títulos, parágrafos)
const renderTextLines = (formatted) => {
  const lines = formatted.split('\n');

  return lines.map((line, index) => {
    const trimmedLine = line.trim();
    
    // Lista numerada (1. ou 1) ou 1), etc.)
    if (/^\d+[\)\.]\s+/.test(trimmedLine)) {
      const match = trimmedLine.match(/^(\d+[\)\.]\s+)(.+)/);
      return (
        <div key={index} style={{ 
          marginBottom: '2px',
          paddingLeft: '18px',
          position: 'relative',
          lineHeight: '1.4'
        }}>
          <span style={{ 
            position: 'absolute', 
            left: 0,
            fontWeight: 600,
            color: 'inherit',
            fontSize: '14px'
          }}>
            {match[1]}
          </span>
          <span style={{ display: 'block', paddingLeft: '22px' }}>
            {formatLineContent(match[2])}
          </span>
        </div>
      );
    }
    
    // Lista com bullet
    if (/^•\s+/.test(trimmedLine)) {
      const content = trimmedLine.replace(/^•\s+/, '');
      return (
        <div key={index} style={{ 
          marginBottom: '1px',
          paddingLeft: '18px',
          position: 'relative',
          lineHeight: '1.4'
        }}>
          <span style={{ 
            position: 'absolute', 
            left: '6px',
            fontSize: '12px',
            lineHeight: '1.4'
          }}>•</span>
          <span style={{ display: 'block', paddingLeft: '10px' }}>
            {formatLineContent(content)}
          </span>
        </div>
      );
    }
    
    // Linha vazia (espaçamento mínimo)
    if (!trimmedLine) {
      return <div key={index} style={{ height: '2px' }} />;
    }
    
    // Texto em negrito (título)
    if (/^\*\*.*\*\*$/.test(trimmedLine)) {
      const title = trimmedLine.replace(/\*\*/g, '');
      return (
        <div key={index} style={{ 
          fontWeight: 600,
          fontSize: '14px',
          marginTop: '6px',
          marginBottom: '2px',
          color: 'inherit'
        }}>
          {title}
        </div>
      );
    }
    
    // Linha normal (compacta)
    return (
      <div key={index} style={{ marginBottom: '1px', lineHeight: '1.4' }}>
    {formatLineContent(trimmedLine)}
      </div>
    );
  });
};

// Formata conteúdo da linha (código, negrito, etc)
const formatLineContent = (line) => {
  if (!line) return line;
  
  const parts = [];
  let lastIndex = 0;
  let keyCounter = 0;
  
  // Regex combinado para negrito (**texto**) e código (`texto`)
  // Ordenar por posição para processar na ordem correta
  const patterns = [];
  
  // Encontrar todos os padrões de negrito e código
  const boldRegex = /\*\*([^*]+)\*\*/g;
  const codeRegex = /`([^`]+)`/g;
  
  let match;
  while ((match = boldRegex.exec(line)) !== null) {
    patterns.push({
      type: 'bold',
      index: match.index,
      length: match[0].length,
      content: match[1]
    });
  }
  
  while ((match = codeRegex.exec(line)) !== null) {
    patterns.push({
      type: 'code',
      index: match.index,
      length: match[0].length,
      content: match[1]
    });
  }
  
  // Ordenar por posição
  patterns.sort((a, b) => a.index - b.index);
  
  // Verificar sobreposições (código tem prioridade)
  const validPatterns = [];
  for (let i = 0; i < patterns.length; i++) {
    const current = patterns[i];
    let overlaps = false;
    
    for (let j = 0; j < validPatterns.length; j++) {
      const existing = validPatterns[j];
      if (
        (current.index < existing.index + existing.length && 
         current.index + current.length > existing.index)
      ) {
        overlaps = true;
        break;
      }
    }
    
    if (!overlaps) {
      validPatterns.push(current);
    }
  }
  
  // Construir partes formatadas
  for (const pattern of validPatterns) {
    if (pattern.index > lastIndex) {
      // Adicionar texto antes do padrão
      parts.push(formatInlineContent(line.substring(lastIndex, pattern.index), keyCounter++));
    }
    
    // Adicionar o padrão formatado
    if (pattern.type === 'bold') {
      parts.push(
        <strong key={`bold-${keyCounter++}`} style={{
          fontWeight: 600,
          color: 'inherit'
        }}>
          {pattern.content}
        </strong>
      );
    } else if (pattern.type === 'code') {
      parts.push(
        <span key={`code-${keyCounter++}`} style={{
          background: 'rgba(0, 0, 0, 0.08)',
          padding: '1px 4px',
          borderRadius: '3px',
          fontFamily: 'monospace',
          fontSize: '12px',
          color: 'inherit',
          display: 'inline',
          lineHeight: '1.4'
        }}>
          {pattern.content}
        </span>
      );
    }
    
    lastIndex = pattern.index + pattern.length;
  }
  
  // Adicionar texto restante
  if (lastIndex < line.length) {
    parts.push(formatInlineContent(line.substring(lastIndex), keyCounter++));
  }
  
  return parts.length > 0 ? <>{parts}</> : line;
};

// Função auxiliar para processar conteúdo inline simples (sem padrões especiais)
const formatInlineContent = (text, key) => {
  if (!text) return null;
  return <span key={`text-${key}`}>{text}</span>;
};

// Context para controlar o chat externamente
export const ChatBotContext = React.createContext({
  openChat: () => {},
});

// Hook para abrir o chat externamente
export const useChatBotControl = () => {
  const context = React.useContext(ChatBotContext);
  return context;
};

const ChatBot = ({ ref, ...props }) => {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [loadingPhraseIndex, setLoadingPhraseIndex] = useState(0);
  const [loadingPhraseDisplay, setLoadingPhraseDisplay] = useState('');
  const [isAtBottom, setIsAtBottom] = useState(true);
  // Em telas estreitas 500px estoura a viewport e corta o chat
  const [drawerWidth, setDrawerWidth] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < 560 ? '100%' : 500
  );
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const textAreaRef = useRef(null);
  const typingIntervalRef = useRef(null);
  const phraseChangeIntervalRef = useRef(null);
  const currentCharIndexRef = useRef(0);
  const currentPhraseIndexRef = useRef(0);
  
  // Frases animadas durante o carregamento
  const loadingPhrases = [
    'ChatBot.Loading.Interpreting',
    'ChatBot.Loading.Processing',
    'ChatBot.Loading.Analyzing',
    'ChatBot.Loading.Searching',
    'ChatBot.Loading.Generating',
    'ChatBot.Loading.Thinking'
  ];
  
  // Expor função para abrir o chat
  const openChat = React.useCallback(() => {
    setIsOpen(true);
  }, []);

  // Expor função via ref (React 19: ref é uma prop normal)
  React.useImperativeHandle(ref, () => ({
    openChat
  }), [openChat]);

  // Registrar função de controle
  React.useEffect(() => {
    setChatControlRef({ openChat });
  }, [openChat]);
  
      // Sem opções de estilo – agente define o tom

  const {
    messages,
    isLoading,
    isStreaming,
    error,
    clearError,
    limitInfo,
    sendStreamingMessage,
    clearChat,
    cancelRequest,
    initializeChat,
    reloadLimit,
  } = useChatBot();

  useEffect(() => {
    const handleResize = () => {
      setDrawerWidth(window.innerWidth < 560 ? '100%' : 500);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Scroll para a última mensagem
  const scrollToBottom = React.useCallback((behavior = 'auto') => {
    messagesEndRef.current?.scrollIntoView({ behavior, block: 'end' });
  }, []);

  // Detecta se o usuário saiu do fim da conversa para ler algo acima
  const handleMessagesScroll = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setIsAtBottom(distanceFromBottom < 80);
  };

  // Só acompanha a geração se o usuário já estiver no fim.
  // Caso contrário ele perderia o trecho que está lendo a cada token recebido.
  useEffect(() => {
    if (isAtBottom) {
      scrollToBottom();
    }
  }, [messages, isAtBottom, scrollToBottom]);

  // Inicializar chat quando abrir e recarregar limite
  useEffect(() => {
    if (isOpen) {
      // Recarregar limite sempre que o drawer abrir
      reloadLimit();
      
      // Inicializar chat apenas se não houver mensagens
      if (messages.length === 0) {
        initializeChat();
      }
    }
  }, [isOpen, initializeChat, messages.length, reloadLimit]);

  // Animação de frases durante o carregamento com efeito de digitação
  useEffect(() => {
    if (isLoading || isStreaming) {
      currentPhraseIndexRef.current = 0;
      setLoadingPhraseIndex(0);
      setLoadingPhraseDisplay('');
      currentCharIndexRef.current = 0;
      
      const startTyping = (phraseIndex) => {
        // Limpar intervalo anterior se existir
        if (typingIntervalRef.current) {
          clearInterval(typingIntervalRef.current);
          typingIntervalRef.current = null;
        }
        
        const phraseKey = loadingPhrases[phraseIndex];
        const phrase = t(phraseKey);
        currentCharIndexRef.current = 0;
        setLoadingPhraseDisplay('');
        
        typingIntervalRef.current = setInterval(() => {
          if (currentCharIndexRef.current < phrase.length) {
            setLoadingPhraseDisplay(phrase.substring(0, currentCharIndexRef.current + 1));
            currentCharIndexRef.current++;
          } else {
            if (typingIntervalRef.current) {
              clearInterval(typingIntervalRef.current);
              typingIntervalRef.current = null;
            }
          }
        }, 80); // Digita uma letra a cada 80ms
      };
      
      // Iniciar digitação da primeira frase
      startTyping(0);
      
      // Trocar de frase após um tempo (esperando a digitação terminar + um pouco mais)
      const intervalTime = isStreaming ? 4000 : 3500;
      
      phraseChangeIntervalRef.current = setInterval(() => {
        if (typingIntervalRef.current) {
          clearInterval(typingIntervalRef.current);
          typingIntervalRef.current = null;
        }
        currentPhraseIndexRef.current = (currentPhraseIndexRef.current + 1) % loadingPhrases.length;
        setLoadingPhraseIndex(currentPhraseIndexRef.current);
        startTyping(currentPhraseIndexRef.current);
      }, intervalTime);
    } else {
      setLoadingPhraseDisplay('');
      if (typingIntervalRef.current) {
        clearInterval(typingIntervalRef.current);
        typingIntervalRef.current = null;
      }
      if (phraseChangeIntervalRef.current) {
        clearInterval(phraseChangeIntervalRef.current);
        phraseChangeIntervalRef.current = null;
      }
    }
    
    return () => {
      if (typingIntervalRef.current) {
        clearInterval(typingIntervalRef.current);
        typingIntervalRef.current = null;
      }
      if (phraseChangeIntervalRef.current) {
        clearInterval(phraseChangeIntervalRef.current);
        phraseChangeIntervalRef.current = null;
      }
    };
  }, [isLoading, isStreaming, loadingPhrases.length, t]);

  const isGenerating = isLoading || isStreaming;
  const limitReached = !!(limitInfo && !limitInfo.canSendMessage);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isGenerating) return;

    const message = inputValue.trim();
    setInputValue('');
    setIsAtBottom(true); // enviar sempre traz o usuário de volta ao fim
    
    // Focar no textarea após enviar
    setTimeout(() => {
      textAreaRef.current?.focus();
    }, 100);

    await sendStreamingMessage(message);
  };

  // onKeyDown: onKeyPress está depreciado e não dispara de forma confiável
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
      return;
    }
    if (e.key === 'Escape' && isGenerating) {
      e.preventDefault();
      cancelRequest();
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };
  
  // Context value
  const contextValue = React.useMemo(() => ({
    openChat
  }), [openChat]);

  const handleClear = () => {
    clearChat();
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString(navigator.language || 'pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderMessage = (message, index) => {
    const isUser = message.role === 'user';
    const isAssistant = message.role === 'assistant';
    const isSystem = message.role === 'system';

    if (isSystem) return null; // Não renderizar mensagens do sistema

    // Verificar se é a última mensagem do assistente e está em streaming (e ainda não tem conteúdo)
    const isLastMessage = index === messages.length - 1;
    const isLastAssistantMessage = isAssistant && isLastMessage;
    const showLoadingInMessage = isLastAssistantMessage && message.isStreaming && (!message.content || message.content.trim() === '');

    return (
      <MessageContainer key={index} isUser={isUser} style={{ alignSelf: isUser ? 'flex-end' : 'flex-start' }}>
        <Space align="start" style={{ width: '100%' }}>
          <Avatar 
            size="small" 
            icon={isUser ? <UserOutlined /> : <FriendlyAIIcon size={16} />}
            style={{ 
              backgroundColor: isUser ? '#4085c6' : 'transparent',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <MessageBubble isUser={isUser} isStreaming={message.isStreaming && isLastMessage}>
              <div style={{ 
                whiteSpace: 'pre-wrap',
                color: 'inherit',
                fontSize: '14px',
                lineHeight: '1.4'
              }}>
                {showLoadingInMessage ? (
                  <LoadingPhrase key={loadingPhraseIndex}>
                    {loadingPhraseDisplay}
                    <span className="cursor-blink">|</span>
                  </LoadingPhrase>
                ) : (
                  <>
                    {message.content ? (
                      <FormattedMessage content={message.content} />
                    ) : ''}
                    {isLastAssistantMessage && message.isStreaming && message.content && message.content.trim() !== '' && (
                      <span className="cursor-blink">|</span>
                    )}
                  </>
                )}
              </div>
            </MessageBubble>
            <Text 
              type="secondary" 
              style={{ 
                fontSize: '11px', 
                marginTop: '4px',
                display: 'block'
              }}
            >
              {formatTime(message.timestamp)}
            </Text>
          </div>
        </Space>
      </MessageContainer>
    );
  };

  return (
    <ChatBotContext.Provider value={contextValue}>
      {/* Drawer do ChatBot */}
      <Drawer
        title={
          <HeaderContainer>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Space>
                <Avatar 
                  icon={<FriendlyAIIcon size={28} />} 
                  style={{ 
                    backgroundColor: 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }} 
                />
                <div>
                  <Title level={5} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    FlowIA - {t('ChatBot.Title')}
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '2px 8px',
                      background: 'linear-gradient(135deg, #ff7875 0%, #ff4d4f 100%)',
                      border: '1px solid #ff4d4f',
                      borderRadius: '10px',
                      fontSize: '9px',
                      fontWeight: 700,
                      color: '#ffffff',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      boxShadow: '0 2px 4px rgba(255, 77, 79, 0.3)'
                    }}>
                      {t('ChatBot.Beta')}
                    </span>
                  </Title>
                </div>
              </Space>
            </Space>
          </HeaderContainer>
        }
        placement="right"
        onClose={handleClose}
        open={isOpen}
        width={drawerWidth}
        closable={true}
        extra={
          <Space size="small" style={{ alignItems: 'center' }}>
            <Tooltip title={t('ChatBot.ClearChat')}>
              <Button
                type="text"
                icon={<ClearOutlined />}
                onClick={handleClear}
                size="small"
                disabled={messages.length === 0}
                aria-label={t('ChatBot.ClearChat')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px'
                }}
              />
            </Tooltip>
          </Space>
        }
        styles={{
          header: {
            background: '#ffffff',
            borderBottom: '1px solid #f0f0f0',
            padding: '16px 24px'
          },
          body: { 
            padding: 0,
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <ChatContainer>
          {/* Área de mensagens */}
          <div
            ref={messagesContainerRef}
            onScroll={handleMessagesScroll}
            role="log"
            aria-live="polite"
            aria-relevant="additions text"
            aria-label={t('ChatBot.Title')}
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              minHeight: 0
            }}
          >
            {messages.length === 0 && (
              <div style={{ 
                textAlign: 'center', 
                padding: '60px 20px',
                color: '#8c8c8c',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '300px'
              }}>
                <div style={{
                  background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                  borderRadius: '50%',
                  padding: '24px',
                  marginBottom: '24px',
                  boxShadow: '0 4px 12px rgba(82, 196, 26, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <FriendlyAIIcon size={72} />
                </div>
                <Text style={{ 
                  color: '#666666', 
                  fontSize: '16px',
                  fontWeight: '500'
                }}>
                  {t('ChatBot.WelcomeMessage')}
                </Text>
              </div>
            )}

            {messages.map(renderMessage)}
            
            {/* Mostrar frase animada apenas se não há mensagem do assistente ainda ou se a última mensagem não é do assistente */}
            {(isLoading || isStreaming) && (
              messages.length === 0 || 
              messages[messages.length - 1]?.role !== 'assistant' || 
              (!messages[messages.length - 1]?.isStreaming && !messages[messages.length - 1]?.content)
            ) && (
              <MessageContainer key="loading-message" isUser={false} style={{ alignSelf: 'flex-start' }}>
                <Space align="start" style={{ width: '100%' }}>
                  <Avatar 
                    size="small" 
                    icon={<FriendlyAIIcon size={16} />}
                    style={{ 
                      backgroundColor: 'transparent',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <MessageBubble isUser={false}>
                      <LoadingPhrase key={loadingPhraseIndex}>
                        {loadingPhraseDisplay}
                        <span className="cursor-blink">|</span>
                      </LoadingPhrase>
                    </MessageBubble>
                  </div>
                </Space>
              </MessageContainer>
            )}
            
            {error && (
              <Alert
                message={t('ChatBot.Error')}
                description={error}
                type="error"
                showIcon
                closable
                onClose={clearError}
              />
            )}

            {limitInfo && limitInfo.warningThreshold && limitInfo.canSendMessage && (
              <Alert
                message={t('ChatBot.LimitWarning')}
                description={t('ChatBot.LimitWarningDescription', { 
                  remaining: limitInfo.remaining, 
                  limit: limitInfo.limit 
                })}
                type="warning"
                showIcon
                style={{ marginBottom: '16px' }}
              />
            )}

            {limitInfo && !limitInfo.canSendMessage && (
              <Alert
                message={t('ChatBot.LimitExceededTitle')}
                description={t('ChatBot.LimitExceededDescription', { 
                  limit: limitInfo.limit, 
                  current: limitInfo.currentCount 
                })}
                type="error"
                showIcon
                style={{ marginBottom: '16px' }}
              />
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Volta ao fim quando o usuário subiu para reler algo */}
          {!isAtBottom && messages.length > 0 && (
            <div style={{ position: 'relative', height: 0 }}>
              <Button
                shape="circle"
                icon={<ArrowDownOutlined />}
                onClick={() => {
                  setIsAtBottom(true);
                  scrollToBottom('smooth');
                }}
                aria-label={t('ChatBot.ScrollToBottom')}
                style={{
                  position: 'absolute',
                  bottom: '12px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 2,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                }}
              />
            </div>
          )}

          <Divider style={{ margin: 0 }} />

          {/* Área de input */}
          <InputContainer>
            <Space.Compact style={{ width: '100%' }}>
              <TextArea
                ref={textAreaRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('ChatBot.InputPlaceholder')}
                autoSize={{ minRows: 1, maxRows: 4 }}
                disabled={limitReached}
                aria-label={t('ChatBot.InputPlaceholder')}
                style={{ resize: 'none' }}
              />
              {isGenerating ? (
                <Tooltip title={t('ChatBot.Cancel')}>
                  <Button
                    danger
                    type="primary"
                    icon={<StopOutlined />}
                    onClick={cancelRequest}
                    aria-label={t('ChatBot.Cancel')}
                    style={{ height: 'auto' }}
                  />
                </Tooltip>
              ) : (
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={handleSendMessage}
                  disabled={!inputValue.trim() || limitReached}
                  style={{ height: 'auto' }}
                />
              )}
            </Space.Compact>
            {limitInfo && limitInfo.canSendMessage && (
              <Text 
                type="secondary" 
                style={{ 
                  fontSize: '11px', 
                  marginTop: '8px',
                  display: 'block',
                  textAlign: 'center',
                  color: limitInfo.warningThreshold ? '#faad14' : '#8c8c8c'
                }}
              >
                {t('ChatBot.MessagesRemaining', { 
                  remaining: limitInfo.remaining, 
                  limit: limitInfo.limit 
                })}
              </Text>
            )}
            <Text 
              type="secondary" 
              style={{ 
                fontSize: '11px', 
                marginTop: '4px',
                display: 'block',
                textAlign: 'center',
                color: '#8c8c8c'
              }}
            >
              {t('ChatBot.DemoVersion')}
            </Text>
          </InputContainer>
        </ChatContainer>
      </Drawer>
    </ChatBotContext.Provider>
  );
};

export default ChatBot;
